import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../../../client/components/ui/sg-snackbar.js';
import { deconstructTaetigkeitsschluessel } from '@martinus/taetigkeit/api/calculateTaetigkeitsschluessel.js';
import { calculateErfahrungsstufe } from '../../../api/calculateErfahrungsstufe.js';
import type { AppliedContract } from '../../../entities/AppliedContract.js';

export type EingruppierungPath = 'agEingruppierung1' | 'agEingruppierung2';

export type ContractForm = FunctionalForm<EntityData<AppliedContract>>;

/** Displays the calculated erfahrungsstufe with its phase start */
export const ErfahrungsstufeText = (contract: Maybe<AppliedContract>, eingruppierung: '1' | '2') => {
  if (!contract) return nothing;
  const res = calculateErfahrungsstufe(contract, eingruppierung, contract.appliedDate);
  if (!res) return nothing;

  return html`<p><b>${res.stufe}</b> seit dem ${formatCalendarDay(res.startOfPhase, 'dd.MM.yyyy')}</p>`;
};

const TAETIGKEITSSCHLUESSEL_LABELS: Record<string, string> = {
  taetigkeit: 'Tätigkeit',
  schulabschluss: 'Höchster allgemeinbildender Schulabschluss',
  berufsabschluss: 'Höchster beruflicher Ausbildungsabschluss',
  arbeitnehmerueberlassung: 'Arbeitnehmerüberlassung',
  vertragsform: 'Vertragsform',
};

/** Displays the taetigkeitsschluessel in its parts, marking invalid parts, with a copy to clipboard button */
export const TaetigkeitsschluesselDisplay = (schluessel: Maybe<string>) => {
  if (!schluessel) return html`<p>—</p>`;

  const parts = deconstructTaetigkeitsschluessel(schluessel);
  const hasError = (val: string) => val === '9' || val === '99999';

  return html`
    <div class="flex flex-row flex-wrap items-center gap-1">
      ${(Object.entries(parts) as [string, string][]).map(
        ([key, val]) => html`
          <div
            class="tooltip rounded border-2 p-1 cursor-default ${hasError(val) ? 'border-error' : 'border-success'}"
            ${hoverTooltip()}
          >
            <div class="tooltip-content">${TAETIGKEITSSCHLUESSEL_LABELS[key]}</div>
            <span>${val}</span>
          </div>
        `,
      )}
      <button
        class="btn btn-ghost btn-sm btn-square"
        aria-label="Tätigkeitsschlüssel kopieren"
        @click=${() => {
          void navigator.clipboard.writeText(schluessel);
          void SgSnackbar.show('Tätigkeitsschlüssel kopiert');
        }}
      >
        <span class="icon-[material-symbols--content-copy-outline] text-lg"></span>
      </button>
    </div>
  `;
};

export { KostenstellenEditor } from '../KostenstellenEditor.js';
